import { Carousel, ActionCard } from '@pcln/horizon'
import { useEffect, useMemo } from 'react'

import analytics from '@/shared-utils/analytics'

import { fireRecentSearchCardClickEvent, fireRecentSearchLoadEvent } from './ga4'
import type { RecentTripSearches } from './types'
import { getRecentSearchDisplayProps } from './utils'

const PRODUCT_ICON: Record<string, string> = {
  FLY: 'Flight',
  DRIVE: 'Car',
  STAY: 'Hotel',
} as const

export default function RecentSearchCarousel({
  pageName,
  items,
}: Readonly<{
  pageName: 'homepage' | 'landing'
  items: ReadonlyArray<RecentTripSearches>
}>) {
  useEffect(() => {
    if (items.length > 0) {
      fireRecentSearchLoadEvent({ searchItems: items, pageName })
    }
  }, [items, pageName])

  const slides = useMemo(
    () =>
      items.reduce<{ id: string; childrenNodes: React.ReactNode }[]>((validSlides, item, index) => {
        try {
          const { overline, heading, actionText, relativeUrl } = getRecentSearchDisplayProps(item)
          const mediaContent = PRODUCT_ICON[item.productType]
          return [
            ...validSlides,
            {
              id: `recent-search-${index}-${overline}`,
              childrenNodes: (
                <ActionCard
                  role='link'
                  href={relativeUrl}
                  anchorProps={{ target: '_blank', rel: 'noopener noreferrer' }}
                  eyebrow={overline}
                  heading={heading}
                  body={actionText}
                  mediaType='icon'
                  headingLineClamp={1}
                  mediaContent={mediaContent}
                  onClick={() => {
                    fireRecentSearchCardClickEvent({
                      productType: item.productType,
                      pageName: pageName,
                    })
                  }}
                  className='w-full'
                />
              ),
            },
          ]
        } catch (err) {
          analytics.logError({
            message: 'Error in getRecentSearchDisplayProps',
            errorMessage: err instanceof Error ? err.message : String(err),
            logObject: {
              error: JSON.stringify(err),
              item,
            },
          })
          return validSlides
        }
      }, []),
    [items, pageName],
  )

  return (
    <Carousel
      ariaLabel='Your recent searches'
      dragFree
      numSlides='one-two-three'
      slides={slides}
      slideWidth='same'
      pagination='numbers'
      type='content'
    />
  )
}
